import { type ITotalRowData, TotalRow } from '@/components/ImpressionByType/TotalRow';
import TablePagination from '@/components/Table/TablePagination';
import { SxProps, Theme } from '@mui/system';
import {
  gridFilteredTopLevelRowCountSelector,
  gridPageCountSelector,
  gridPaginationModelSelector,
  gridPaginationRowRangeSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import LoadingBackdrop from '../LoadingBackdrop';

interface IProps {
  loading?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
  rowsPerPageOptions: number[];
  paginationDisabled?: boolean;
  totalRowData?: ITotalRowData;
}

const Pagination = ({
  loading,
  className,
  sx,
  rowsPerPageOptions = [15, 25, 50],
  paginationDisabled,
  totalRowData,
}: IProps) => {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const visibleTopLevelRowCount = useGridSelector(apiRef, gridFilteredTopLevelRowCountSelector);
  const rowCount = useMemo(() => {
    let _ref, _rootProps$rowCount;
    return (_ref =
      (_rootProps$rowCount = rootProps.rowCount) != null ? _rootProps$rowCount : visibleTopLevelRowCount) != null
      ? _ref
      : 0;
  }, [rootProps.rowCount, visibleTopLevelRowCount]);
  const lastPage = useMemo(
    () => Math.floor(rowCount / (paginationModel.pageSize || 1)),
    [rowCount, paginationModel.pageSize],
  );
  const handlePageSizeChange = useCallback(
    (event: Event) => {
      const pageSize = Number((event.target as HTMLInputElement).value);
      apiRef.current.setPageSize(pageSize);
    },
    [apiRef],
  );
  const handlePageChange = useCallback(
    (_: Event, page: number) => {
      apiRef.current.setPage(page);
    },
    [apiRef],
  );
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const range = useGridSelector(apiRef, gridPaginationRowRangeSelector) || { firstRowIndex: 0, lastRowIndex: 0 };

  const { firstRowIndex: start, lastRowIndex: end } = range;

  return (
    <div className='flex flex-col w-[100%] gap-3'>
      {totalRowData && <TotalRow totalRowData={totalRowData} />}
      <TablePagination
        className={classNames('w-full relative print:hidden', className)}
        totalPages={pageCount}
        rowsPerPage={paginationModel.pageSize}
        total={rowCount}
        rowsPerPageOptions={rowsPerPageOptions}
        page={paginationModel.page <= lastPage ? paginationModel.page + 1 : lastPage}
        start={start + 1 || 0}
        end={Math.min(end + 1, rowCount) || 0}
        onPageChange={(event, newPage) => {
          handlePageChange(event, newPage - 1);
        }}
        handleChangeRowsPerPage={handlePageSizeChange}
        sx={sx}
        paginationDisabled={paginationDisabled}
      />
      {loading ? <LoadingBackdrop /> : null}
    </div>
  );
};

export default Pagination;
